/** #55 voice notes — hold-to-record ≤60 s via MediaRecorder. */
import { useCallback, useRef, useState } from "react";

export const VOICE_MAX_MS = 60_000;

export interface VoiceRecorder {
  recording: boolean;
  elapsedMs: number;
  /** Start capture; resolves false when the mic permission is refused. */
  start(): Promise<boolean>;
  /** Stop and get the audio blob (null when canceled/empty). */
  stop(): Promise<Blob | null>;
  cancel(): void;
}

export function useVoiceRecorder(): VoiceRecorder {
  const [recording, setRecording] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const timer = useRef<number>();
  const startedAt = useRef(0);
  const stopResolve = useRef<((b: Blob | null) => void) | null>(null);
  const canceled = useRef(false);

  const teardown = useCallback(() => {
    window.clearInterval(timer.current);
    recorder.current?.stream.getTracks().forEach((t) => t.stop());
    recorder.current = null;
    setRecording(false);
    setElapsedMs(0);
  }, []);

  const start = useCallback(async (): Promise<boolean> => {
    if (recorder.current) return true;
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      return false;
    }
    canceled.current = false;
    chunks.current = [];
    const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
    const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    recorder.current = rec;
    rec.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.current.push(e.data);
    };
    rec.onstop = () => {
      const blob =
        canceled.current || chunks.current.length === 0
          ? null
          : new Blob(chunks.current, { type: rec.mimeType || "audio/webm" });
      stopResolve.current?.(blob);
      stopResolve.current = null;
      teardown();
    };
    rec.start(250);
    startedAt.current = Date.now();
    setRecording(true);
    timer.current = window.setInterval(() => {
      const ms = Date.now() - startedAt.current;
      setElapsedMs(ms);
      if (ms >= VOICE_MAX_MS) rec.stop(); // ≤60 s, hard client cap (#55)
    }, 200);
    return true;
  }, [teardown]);

  const stop = useCallback((): Promise<Blob | null> => {
    const rec = recorder.current;
    if (!rec || rec.state === "inactive") return Promise.resolve(null);
    return new Promise((resolve) => {
      stopResolve.current = resolve;
      rec.stop();
    });
  }, []);

  const cancel = useCallback(() => {
    canceled.current = true;
    const rec = recorder.current;
    if (rec && rec.state !== "inactive") rec.stop();
    else teardown();
  }, [teardown]);

  return { recording, elapsedMs, start, stop, cancel };
}
